import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, CardBody, Row, Col, Button, Spinner, Alert, Input, Label, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from 'reactstrap';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import api from '../helpers/api';
import {
  createEscalationPolicy,
  updateEscalationPolicy,
  clearEscalationMessages,
} from '../store/escalation/actions';

const TARGET_TYPES = (t) => [
  { value: 'WORK_ORDER', label: t('shared.target_type_work_order', 'Work Order') },
  { value: 'SERVICE_REQUEST', label: t('shared.target_type_service_request', 'Service Request') },
  { value: 'ASSET', label: t('shared.target_type_asset', 'Asset') },
];

const WO_STATUSES = ['SUBMITTED', 'PENDING', 'IN_PROGRESS', 'ON_HOLD'];
const SR_STATUSES = ['PENDING', 'APPROVED'];
const URGENCY_LEVELS = ['HIGH', 'MEDIUM', 'LOW'];
const CRITICALITY_LEVELS = ['HIGH', 'MEDIUM', 'LOW'];

const CONDITION_TYPE_LABELS = (t) => ({
  OVERDUE: t('shared.condition_overdue', 'Overdue'),
  TIME_IN_STATUS: t('shared.condition_time_in_status', 'Time in Status'),
  PRIORITY: t('shared.condition_priority', 'Priority'),
  ASSET_CRITICALITY: t('shared.condition_asset_criticality', 'Asset Criticality'),
  ASSET_DOWNTIME: t('shared.condition_asset_downtime', 'Asset Downtime'),
});

const getConditionTypesForTarget = (targetType, t) => {
  const types = [];
  if (targetType === 'WORK_ORDER') {
    types.push({ value: 'OVERDUE', label: t('shared.condition_overdue', 'Overdue') });
    types.push({ value: 'TIME_IN_STATUS', label: t('shared.condition_time_in_status', 'Time in Status') });
    types.push({ value: 'PRIORITY', label: t('shared.condition_priority', 'Priority') });
    types.push({ value: 'ASSET_CRITICALITY', label: t('shared.condition_asset_criticality', 'Asset Criticality') });
  } else if (targetType === 'SERVICE_REQUEST') {
    types.push({ value: 'TIME_IN_STATUS', label: t('shared.condition_time_in_status', 'Time in Status') });
    types.push({ value: 'PRIORITY', label: t('shared.condition_priority', 'Priority') });
    types.push({ value: 'ASSET_CRITICALITY', label: t('shared.condition_asset_criticality', 'Asset Criticality') });
  } else if (targetType === 'ASSET') {
    types.push({ value: 'ASSET_DOWNTIME', label: t('shared.condition_asset_downtime', 'Asset Downtime Duration') });
    types.push({ value: 'ASSET_CRITICALITY', label: t('shared.condition_asset_criticality', 'Asset Criticality') });
  }
  return types;
};

const getStatusesForTarget = (targetType) => {
  if (targetType === 'WORK_ORDER') return WO_STATUSES;
  if (targetType === 'SERVICE_REQUEST') return SR_STATUSES;
  return [];
};

// ─── Policy Summary Generator ──────────────────────────────────
const getAllTargetsLabel = (targetType, t) => {
  switch (targetType) {
    case 'WORK_ORDER':
      return t('escalation.summary_all_work_orders', 'all Work Orders');
    case 'SERVICE_REQUEST':
      return t('escalation.summary_all_service_requests', 'all Service Requests');
    case 'ASSET':
      return t('escalation.summary_all_assets', 'all Assets');
    default:
      return targetType;
  }
};

const buildPolicySummary = (form, conditions, levels, orgUsers, orgTeams, t) => {
  const targetLabel = TARGET_TYPES(t).find(tt => tt.value === form.targetType)?.label || form.targetType;

  // Conditions summary
  let conditionParts = [];
  conditions.forEach(c => {
    switch (c.conditionType) {
      case 'PRIORITY':
        if (c.urgencyLevel) {
          const urgencyLabel = t(`shared.${c.urgencyLevel.toLowerCase()}`, c.urgencyLevel);
          conditionParts.push(`${t('shared.condition_priority', 'Priority')} = ${urgencyLabel}`);
        }
        break;
      case 'ASSET_CRITICALITY':
        if (c.assetCriticality) {
          const critLabel = t(`shared.${c.assetCriticality.toLowerCase()}`, c.assetCriticality);
          conditionParts.push(`${t('escalation.asset_criticality', 'Asset Criticality')} = ${critLabel}`);
        }
        break;
      case 'TIME_IN_STATUS':
        if (c.targetStatus && c.thresholdHours) {
          const statusLabel = t(`shared.${c.targetStatus.toLowerCase()}`, c.targetStatus.replace('_', ' '));
          conditionParts.push(t('escalation.summary_in_status', 'In {{status}} > {{hours}}h', { status: statusLabel, hours: c.thresholdHours }));
        }
        break;
      case 'OVERDUE':
        conditionParts.push(t('shared.condition_overdue', 'Overdue'));
        break;
      case 'ASSET_DOWNTIME':
        if (c.thresholdHours) {
          conditionParts.push(t('escalation.summary_offline', 'Offline > {{hours}}h', { hours: c.thresholdHours }));
        }
        break;
      default:
        break;
    }
  });

  const conditionStr = conditionParts.length > 0
    ? conditionParts.join(` ${t('escalation.summary_and', 'AND')} `)
    : getAllTargetsLabel(form.targetType, t);

  // Levels summary
  const levelLabel = t('escalation.level', 'Level');
  const levelParts = levels
    .filter(l => l.thresholdHours !== '' && l.thresholdHours !== null && l.thresholdHours !== undefined)
    .map((l, i) => {
      const userNames = (l.notifyUserIds || [])
        .map(uid => orgUsers.find(u => u.id === uid)?.name || t('escalation.summary_user_id', 'User #{{id}}', { id: uid }))
        .join(', ');
      const teamNames = (l.notifyTeamIds || [])
        .map(tid => {
          const tm = orgTeams.find(x => x.id === tid);
          return tm ? `${tm.name}` : t('escalation.summary_team_id', 'Team #{{id}}', { id: tid });
        })
        .join(', ');
      const recipientStr = [userNames, teamNames].filter(Boolean).join(' + ');
      return `${levelLabel} ${i + 1} (${l.thresholdHours}h)${recipientStr ? ` \u2192 ${recipientStr}` : ''}`;
    });

  return { targetLabel, conditionStr, levelParts };
};

const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    borderColor: hasError ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#ced4da'),
    boxShadow: hasError
      ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
      : (state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none'),
    '&:hover': { borderColor: '#86b7fe' }
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
    color: state.isSelected || state.isFocused ? 'white' : '#212529',
    '&:active': { backgroundColor: '#2e669a' }
  })
});

const multiSelectStyles = (hasError) => ({
  ...selectStyles(hasError),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#3980c0',
    borderRadius: '4px'
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'white',
    padding: '2px 6px'
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'white',
    '&:hover': {
      backgroundColor: '#2e669a',
      color: 'white'
    }
  })
});

const EscalationPolicyForm = ({ policy, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { saving, saveError, saveSuccess } = useSelector((state) => state.Escalation);

  const [form, setForm] = useState({
    name: '',
    description: '',
    targetType: 'WORK_ORDER',
  });
  const [enableConfirmModal, setEnableConfirmModal] = useState(false);
  const [conditions, setConditions] = useState([
    { conditionType: 'OVERDUE', urgencyLevel: '', assetCriticality: '', targetStatus: '', thresholdHours: '' },
  ]);
  const [levels, setLevels] = useState([
    { levelOrder: 1, thresholdHours: 0, notifyUserIds: [], notifyTeamIds: [] },
  ]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgTeams, setOrgTeams] = useState([]);
  const [errors, setErrors] = useState({});
  const [targetTypeChangeModal, setTargetTypeChangeModal] = useState(null);

  // Populate form when editing
  useEffect(() => {
    if (policy) {
      setForm({
        name: policy.name || '',
        description: policy.description || '',
        targetType: policy.targetType || 'WORK_ORDER',
      });
      setConditions(
        (policy.conditions || []).map((c) => ({
          conditionType: c.conditionType,
          urgencyLevel: c.urgencyLevel || '',
          assetCriticality: c.assetCriticality || '',
          targetStatus: c.targetStatus || '',
          thresholdHours: c.thresholdHours ?? '',
        }))
      );
      setLevels(
        (policy.levels || []).map((l) => ({
          levelOrder: l.levelOrder,
          thresholdHours: l.thresholdHours,
          notifyUserIds: l.notifyUsers?.map((u) => u.id) || [],
          notifyTeamIds: l.notifyTeams?.map((t) => t.id) || [],
        }))
      );
    }
  }, [policy]);

  // Fetch org users + teams for recipient picker
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [userRes, teamRes] = await Promise.allSettled([
          api.get('user/get-all'),
          api.get('team/all'),
        ]);
        if (userRes.status === 'fulfilled') {
          const users = userRes.value.data?.response || [];
          setOrgUsers(Array.isArray(users) ? users : []);
        }
        if (teamRes.status === 'fulfilled') {
          const teams = teamRes.value.data?.response || [];
          setOrgTeams(Array.isArray(teams) ? teams : []);
        }
      } catch (err) {
        console.error('Failed to fetch recipient options:', err);
      }
    };
    fetchOptions();
  }, []);

  // ─── Policy summary (auto-generated) ──────────────────────────
  const summary = useMemo(
    () => buildPolicySummary(form, conditions, levels, orgUsers, orgTeams, t),
    [form, conditions, levels, orgUsers, orgTeams, t]
  );

  const validate = useCallback(() => {
    const errs = {};
    if (!form.name.trim()) errs.name = t('escalation.errors.name_required', 'Policy name is required');
    if (levels.length === 0) errs.levels = t('escalation.errors.levels_required', 'At least one escalation level is required');
    levels.forEach((level, i) => {
      if (level.thresholdHours === '' || level.thresholdHours === null || level.thresholdHours === undefined || level.thresholdHours < 0) {
        errs[`level_${i}_hours`] = t('escalation.errors.threshold_required', 'Threshold hours is required');
      }
      // Require at least one recipient — user OR team — at each level.
      const hasUsers = (level.notifyUserIds?.length || 0) > 0;
      const hasTeams = (level.notifyTeamIds?.length || 0) > 0;
      if (!hasUsers && !hasTeams) {
        errs[`level_${i}_recipients`] = t(
          'escalation.errors.recipients_required',
          'Select at least one user or team'
        );
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [form, levels, t]);

  const buildPayload = useCallback(() => ({
    ...(policy?.id ? { id: policy.id } : {}),
    name: form.name,
    description: form.description,
    targetType: form.targetType,
    conditions: conditions.map((c) => ({
      conditionType: c.conditionType,
      urgencyLevel: c.conditionType === 'PRIORITY' ? c.urgencyLevel : null,
      assetCriticality: c.conditionType === 'ASSET_CRITICALITY' ? c.assetCriticality : null,
      targetStatus: c.conditionType === 'TIME_IN_STATUS' ? c.targetStatus : null,
      thresholdHours: ['TIME_IN_STATUS', 'ASSET_DOWNTIME'].includes(c.conditionType) ? parseInt(c.thresholdHours) : null,
    })),
    levels: levels.map((l, i) => ({
      levelOrder: i + 1,
      thresholdHours: parseInt(l.thresholdHours),
      notifyUserIds: l.notifyUserIds || [],
      notifyTeamIds: l.notifyTeamIds || [],
    })),
  }), [form, conditions, levels, policy]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    if (!policy?.id) {
      // New policy — show enable confirmation modal
      setEnableConfirmModal(true);
      return;
    }
    // Editing existing policy — save directly
    dispatch(clearEscalationMessages());
    dispatch(updateEscalationPolicy(buildPayload(), onClose));
  }, [validate, policy, dispatch, onClose, buildPayload]);

  const handleConfirmCreate = useCallback(() => {
    setEnableConfirmModal(false);
    dispatch(clearEscalationMessages());
    dispatch(createEscalationPolicy(buildPayload(), onClose));
  }, [dispatch, onClose, buildPayload]);

  // ─── Target type change with confirmation ──────────────────────
  const handleTargetTypeChange = (newType) => {
    if (conditions.length > 0 && newType !== form.targetType) {
      setTargetTypeChangeModal(newType);
    } else {
      setForm({ ...form, targetType: newType });
    }
  };

  const confirmTargetTypeChange = () => {
    const newTypes = getConditionTypesForTarget(targetTypeChangeModal, t);
    setForm({ ...form, targetType: targetTypeChangeModal });
    setConditions([
      { conditionType: newTypes[0]?.value || '', urgencyLevel: '', assetCriticality: '', targetStatus: '', thresholdHours: '' },
    ]);
    setTargetTypeChangeModal(null);
  };

  // Condition helpers
  const addCondition = () => {
    const availableTypes = getConditionTypesForTarget(form.targetType, t);
    if (availableTypes.length === 0) return;
    setConditions([...conditions, { conditionType: availableTypes[0].value, urgencyLevel: '', assetCriticality: '', targetStatus: '', thresholdHours: '' }]);
  };

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index, field, value) => {
    setConditions(conditions.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  // Level helpers
  const addLevel = () => {
    const lastHours = parseInt(levels[levels.length - 1]?.thresholdHours) || 0;
    const nextHours = lastHours + (levels.length === 1 ? 1 : 2);
    setLevels([
      ...levels,
      {
        levelOrder: levels.length + 1,
        thresholdHours: nextHours,
        notifyUserIds: [],
        notifyTeamIds: [],
      },
    ]);
  };

  const removeLevel = (index) => {
    setLevels(levels.filter((_, i) => i !== index));
  };

  const updateLevel = (index, field, value) => {
    setLevels(levels.map((l, i) => i === index ? { ...l, [field]: value } : l));
  };

  const toggleUserInLevel = (levelIndex, userId) => {
    const level = levels[levelIndex];
    const userIds = level.notifyUserIds.includes(userId)
      ? level.notifyUserIds.filter((id) => id !== userId)
      : [...level.notifyUserIds, userId];
    updateLevel(levelIndex, 'notifyUserIds', userIds);
  };

  const conditionTypes = getConditionTypesForTarget(form.targetType, t);
  const statusOptions = getStatusesForTarget(form.targetType);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="d-flex align-items-center mb-3">
            <Button color="link" className="p-0 me-3" onClick={onClose}>
              <FeatherIcon icon="arrow-left" size={20} />
            </Button>
            <h5 className="mb-0 fw-bold">
              {policy ? t('escalation.edit_policy', 'Edit Policy') : t('escalation.create_policy', 'Create Policy')}
            </h5>
          </div>

          {saveError && <Alert color="danger" className="mb-3">{saveError}</Alert>}
          {saveSuccess && <Alert color="success" className="mb-3">{t(saveSuccess, saveSuccess)}</Alert>}

          {/* ─── Policy Summary Strip ─────────────────────────────── */}
          {(form.name || conditions.length > 0 || levels.some(l => (l.thresholdHours !== '' && l.thresholdHours !== null && l.thresholdHours !== undefined) && ((l.notifyUserIds || []).length > 0 || (l.notifyTeamIds || []).length > 0))) && (
            <Card className="mb-3 border-start border-primary border-4">
              <CardBody className="py-2 px-3">
                <small className="text-muted d-block mb-1">
                  <FeatherIcon icon="eye" size={12} className="me-1" />
                  {t('escalation.summary_preview', 'Policy Preview')}
                </small>
                <div className="small">
                  <strong>{form.name || t('escalation.untitled', 'Untitled')}</strong>
                  {' \u2014 '}
                  {t('escalation.summary_when', 'When a')} <Badge color="primary" className="mx-1">{summary.targetLabel}</Badge>
                  {t('escalation.summary_matches', 'matches')} <span className="fw-semibold">[{summary.conditionStr}]</span>
                  {summary.levelParts.length > 0 && (
                    <>
                      {', '}
                      {t('escalation.summary_escalate', 'escalate:')}
                      {summary.levelParts.map((part, i) => (
                        <div key={i} className="ms-3">{part}</div>
                      ))}
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Section 1: Basic Info */}
          <Card className="mb-3">
            <CardBody>
              <h6 className="fw-bold mb-3">
                <FeatherIcon icon="info" size={16} className="me-2" />
                {t('escalation.basic_info', 'Basic Information')}
              </h6>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>{t('escalation.policy_name', 'Policy Name')} *</Label>
                    <Input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      invalid={!!errors.name}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>{t('escalation.target_type', 'Applies To')} *</Label>
                    <Select
                      options={TARGET_TYPES(t)}
                      value={TARGET_TYPES(t).find((tt) => tt.value === form.targetType) || null}
                      onChange={(selected) => handleTargetTypeChange(selected?.value || 'WORK_ORDER')}
                      isSearchable={false}
                      placeholder={t('shared.select', 'Select...')}
                      classNamePrefix="react-select"
                      styles={selectStyles(false)}
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div className="mb-3">
                    <Label>{t('escalation.description', 'Description')}</Label>
                    <Input
                      type="textarea"
                      rows={2}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Section 2: Conditions */}
          <Card className="mb-3">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">
                  <FeatherIcon icon="filter" size={16} className="me-2" />
                  {t('escalation.conditions', 'Conditions')}
                </h6>
                <Button color="outline-primary" size="sm" onClick={addCondition} disabled={conditionTypes.length === 0}>
                  <FeatherIcon icon="plus" size={14} className="me-1" />
                  {t('escalation.add_condition', 'Add Condition')}
                </Button>
              </div>

              {/* Ghost/placeholder row showing available condition structure */}
              {conditions.length === 0 && (
                <div
                  className="border border-dashed rounded p-3 text-center"
                  style={{ borderStyle: 'dashed', cursor: 'pointer', background: '#f8f9fa' }}
                  onClick={addCondition}
                >
                  <p className="text-muted small mb-2">
                    {t('escalation.no_conditions_help', 'No conditions set. This policy will match all items of the selected type.')}
                  </p>
                  <div className="d-flex justify-content-center gap-2 flex-wrap">
                    {conditionTypes.map(ct => (
                      <Badge key={ct.value} color="light" className="text-muted border">
                        {ct.label}
                      </Badge>
                    ))}
                  </div>
                  <small className="text-muted d-block mt-2">
                    {t('escalation.click_to_add_condition', 'Click to add a condition')}
                  </small>
                </div>
              )}

              {conditions.map((cond, idx) => (
                <Row key={idx} className="mb-3 align-items-start">
                  <Col md={3}>
                    <Label>{t('escalation.condition_type', 'Type')}</Label>
                    <Select
                      options={conditionTypes}
                      value={conditionTypes.find((ct) => ct.value === cond.conditionType) || null}
                      onChange={(selected) => updateCondition(idx, 'conditionType', selected?.value || conditionTypes[0]?.value)}
                      isSearchable={false}
                      classNamePrefix="react-select"
                      styles={selectStyles(false)}
                    />
                  </Col>

                  {cond.conditionType === 'PRIORITY' && (
                    <Col md={3}>
                      <Label>{t('escalation.urgency_level', 'Urgency Level')}</Label>
                      <Select
                        options={URGENCY_LEVELS.map((u) => ({ value: u, label: t(`shared.${u.toLowerCase()}`, u) }))}
                        value={cond.urgencyLevel ? { value: cond.urgencyLevel, label: t(`shared.${cond.urgencyLevel.toLowerCase()}`, cond.urgencyLevel) } : null}
                        onChange={(selected) => updateCondition(idx, 'urgencyLevel', selected?.value || '')}
                        isClearable
                        isSearchable={false}
                        placeholder={t('shared.select', 'Select...')}
                        classNamePrefix="react-select"
                        styles={selectStyles(false)}
                      />
                    </Col>
                  )}

                  {cond.conditionType === 'ASSET_CRITICALITY' && (
                    <Col md={3}>
                      <Label>{t('escalation.asset_criticality', 'Criticality')}</Label>
                      <Select
                        options={CRITICALITY_LEVELS.map((c) => ({ value: c, label: t(`shared.${c.toLowerCase()}`, c) }))}
                        value={cond.assetCriticality ? { value: cond.assetCriticality, label: t(`shared.${cond.assetCriticality.toLowerCase()}`, cond.assetCriticality) } : null}
                        onChange={(selected) => updateCondition(idx, 'assetCriticality', selected?.value || '')}
                        isClearable
                        isSearchable={false}
                        placeholder={t('shared.select', 'Select...')}
                        classNamePrefix="react-select"
                        styles={selectStyles(false)}
                      />
                    </Col>
                  )}

                  {cond.conditionType === 'TIME_IN_STATUS' && (
                    <>
                      <Col md={3}>
                        <Label>{t('shared.status', 'Status')}</Label>
                        <Select
                          options={statusOptions.map((s) => ({ value: s, label: t(`shared.${s.toLowerCase()}`, s.replace('_', ' ')) }))}
                          value={cond.targetStatus ? { value: cond.targetStatus, label: t(`shared.${cond.targetStatus.toLowerCase()}`, cond.targetStatus.replace('_', ' ')) } : null}
                          onChange={(selected) => updateCondition(idx, 'targetStatus', selected?.value || '')}
                          isClearable
                          isSearchable={false}
                          placeholder={t('shared.select', 'Select...')}
                          classNamePrefix="react-select"
                          styles={selectStyles(false)}
                        />
                      </Col>
                      <Col md={2}>
                        <Label>{t('escalation.threshold_hours', 'Threshold (hours)')}</Label>
                        <Input
                          type="number"
                          min={1}
                          value={cond.thresholdHours}
                          onChange={(e) => updateCondition(idx, 'thresholdHours', e.target.value)}
                        />
                      </Col>
                    </>
                  )}

                  {cond.conditionType === 'ASSET_DOWNTIME' && (
                    <Col md={3}>
                      <Label>{t('escalation.threshold_hours', 'Threshold (hours)')}</Label>
                      <Input
                        type="number"
                        min={1}
                        value={cond.thresholdHours}
                        onChange={(e) => updateCondition(idx, 'thresholdHours', e.target.value)}
                      />
                    </Col>
                  )}

                  <Col xs="auto" style={{ marginTop: '32px' }}>
                    <button
                      type="button"
                      className="btn btn-link text-danger p-0 d-flex align-items-center justify-content-center"
                      onClick={() => removeCondition(idx)}
                      title={t('shared.remove', 'Remove')}
                      style={{ width: 32, height: 38 }}
                    >
                      <FeatherIcon icon="trash-2" size={18} />
                    </button>
                  </Col>
                </Row>
              ))}
            </CardBody>
          </Card>

          {/* Section 3: Escalation Levels — visual timeline */}
          <Card className="mb-3">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">
                  <FeatherIcon icon="layers" size={16} className="me-2" />
                  {t('escalation.levels', 'Escalation Levels')}
                </h6>
                <Button color="outline-primary" size="sm" onClick={addLevel}>
                  <FeatherIcon icon="plus" size={14} className="me-1" />
                  {t('escalation.add_level', 'Add Level')}
                </Button>
              </div>

              {errors.levels && <Alert color="danger" className="py-1 small">{errors.levels}</Alert>}

              <div className="escalation-timeline">
                {levels.map((level, idx) => {
                  const prevHours = idx > 0 ? parseInt(levels[idx - 1].thresholdHours) || 0 : 0;
                  const currentHours = parseInt(level.thresholdHours) || 0;
                  const delta = currentHours - prevHours;

                  return (
                    <React.Fragment key={idx}>
                      {/* Connector */}
                      <div className="d-flex align-items-center py-2" style={{ paddingLeft: 18 }}>
                        <div style={{ width: 2, height: 24, background: '#dee2e6', marginRight: 12 }} />
                        <small className="text-muted fst-italic">
                          <FeatherIcon icon="clock" size={12} className="me-1" />
                          {idx === 0
                            ? (currentHours > 0
                              ? t('escalation.connector_after', 'If not resolved after {{hours}}h, escalate to...', { hours: currentHours })
                              : t('escalation.connector_immediate', 'Immediately escalate to...'))
                            : (delta > 0
                              ? t('escalation.connector_after', 'If not resolved after {{hours}}h, escalate to...', { hours: delta })
                              : t('escalation.connector_immediate', 'Immediately escalate to...'))}
                        </small>
                      </div>

                      {/* Level card with timeline dot */}
                      <div className="d-flex">
                        {/* Timeline indicator */}
                        <div className="d-flex flex-column align-items-center me-3" style={{ width: 36 }}>
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                            style={{
                              width: 28, height: 28, fontSize: 12, flexShrink: 0,
                              background: idx === 0 ? '#4b7bec' : idx === 1 ? '#f7b731' : '#eb3b5a',
                            }}
                          >
                            {idx + 1}
                          </div>
                          {idx < levels.length - 1 && (
                            <div style={{ width: 2, flexGrow: 1, background: '#dee2e6', minHeight: 20 }} />
                          )}
                        </div>

                        {/* Level content */}
                        <Card className="mb-2 border flex-grow-1">
                          <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <Badge color={idx === 0 ? 'primary' : idx === 1 ? 'warning' : 'danger'}>
                                  {t('escalation.level', 'Level')} {idx + 1}
                                </Badge>
                                {/* Relative timing label */}
                                <span className="text-muted small ms-2">
                                  {currentHours > 0 && (
                                    <>
                                      {currentHours}h {t('escalation.after_trigger', 'after trigger')}
                                      {idx > 0 && delta > 0 && (
                                        <span className="ms-1">
                                          ({delta}h {t('escalation.after_level', 'after Level')} {idx})
                                        </span>
                                      )}
                                    </>
                                  )}
                                </span>
                              </div>
                              {levels.length > 1 && (
                                <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeLevel(idx)} title={t('shared.remove', 'Remove')}>
                                  <FeatherIcon icon="trash-2" size={18} />
                                </button>
                              )}
                            </div>
                            <Row>
                              <Col md={3}>
                                <Label>{t('escalation.threshold_hours', 'Threshold (hours)')} *</Label>
                                <Input
                                  type="number"
                                  min={0}
                                  value={level.thresholdHours}
                                  onChange={(e) => updateLevel(idx, 'thresholdHours', e.target.value)}
                                  invalid={!!errors[`level_${idx}_hours`]}
                                />
                                {errors[`level_${idx}_hours`] && <div className="invalid-feedback">{errors[`level_${idx}_hours`]}</div>}
                                <small className="text-muted">
                                  {t('escalation.threshold_help', 'Hours after trigger condition is met')}
                                </small>
                              </Col>
                              <Col md={9}>
                                <Label>
                                  {t('escalation.notify_recipients', 'Recipients')} *
                                </Label>
                                {errors[`level_${idx}_recipients`] && (
                                  <small className="text-danger ms-2">
                                    {errors[`level_${idx}_recipients`]}
                                  </small>
                                )}
                                <small className="text-muted d-block mb-1">
                                  {t('escalation.notify_users_label', 'Users')}
                                </small>
                                <Select
                                  isMulti
                                  options={orgUsers.map((u) => ({ value: u.id, label: u.name }))}
                                  value={(level.notifyUserIds || []).map((uid) => {
                                    const user = orgUsers.find((u) => u.id === uid);
                                    return { value: uid, label: user?.name || `User #${uid}` };
                                  })}
                                  onChange={(selected) => {
                                    updateLevel(idx, 'notifyUserIds', selected ? selected.map((s) => s.value) : []);
                                  }}
                                  isClearable
                                  isSearchable
                                  placeholder={t('escalation.select_user', 'Select user to add...')}
                                  classNamePrefix="react-select"
                                  styles={multiSelectStyles(!!errors[`level_${idx}_recipients`])}
                                  noOptionsMessage={() => t('business.users.no_users_found', 'No users found')}
                                />
                                <small className="text-muted d-block mb-1 mt-2">
                                  {t('escalation.notify_teams_label', 'Teams')}
                                </small>
                                <Select
                                  isMulti
                                  options={orgTeams.map((tm) => ({
                                    value: tm.id,
                                    label: `${tm.name}${tm.memberCount != null ? ` · ${tm.memberCount} ${t('shared.members', 'members')}` : ''}`,
                                    color: tm.color,
                                  }))}
                                  value={(level.notifyTeamIds || []).map((tid) => {
                                    const tm = orgTeams.find((x) => x.id === tid);
                                    return {
                                      value: tid,
                                      label: tm
                                        ? `${tm.name}${tm.memberCount != null ? ` · ${tm.memberCount} ${t('shared.members', 'members')}` : ''}`
                                        : `Team #${tid}`,
                                      color: tm?.color,
                                    };
                                  })}
                                  onChange={(selected) => {
                                    updateLevel(idx, 'notifyTeamIds', selected ? selected.map((s) => s.value) : []);
                                  }}
                                  isClearable
                                  isSearchable
                                  placeholder={t('escalation.select_team', 'Select team to add...')}
                                  classNamePrefix="react-select"
                                  styles={multiSelectStyles(!!errors[`level_${idx}_recipients`])}
                                  noOptionsMessage={() => t('teams.empty', 'No teams yet')}
                                />
                                <small className="text-muted d-block mt-1">
                                  {t('escalation.team_notify_help', 'All members of the selected teams are notified, deduplicated against the user list.')}
                                </small>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </CardBody>
          </Card>

          {/* Actions */}
          <div className="d-flex justify-content-end gap-2 mb-5">
            <Button color="secondary" outline onClick={onClose} disabled={saving}>
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : null}
              {policy ? t('shared.save', 'Save') : t('escalation.create_policy', 'Create Policy')}
            </Button>
          </div>
        </motion.div>

        {/* Target Type Change Confirmation */}
        <Modal isOpen={!!targetTypeChangeModal} toggle={() => setTargetTypeChangeModal(null)} centered>
          <ModalHeader toggle={() => setTargetTypeChangeModal(null)}>
            {t('escalation.change_target_type', 'Change Entity Type')}
          </ModalHeader>
          <ModalBody>
            {t('escalation.change_target_type_warning',
              'Changing the entity type will clear all existing conditions since different types support different condition options. Continue?')}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setTargetTypeChangeModal(null)}>
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button color="warning" onClick={confirmTargetTypeChange}>
              {t('escalation.change_and_clear', 'Change & Clear Conditions')}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Enable Policy Confirmation (on create) */}
        <Modal isOpen={enableConfirmModal} toggle={() => setEnableConfirmModal(false)} centered>
          <ModalHeader toggle={() => setEnableConfirmModal(false)}>
            {t('escalation.enable_policy', 'Enable Policy')}
          </ModalHeader>
          <ModalBody>
            <p>{t('escalation.confirm_enable_on_create', 'This policy will be enabled and start evaluating escalations for new items.')}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setEnableConfirmModal(false)}>
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button color="success" onClick={handleConfirmCreate} disabled={saving}>
              {saving ? <Spinner size="sm" /> : t('escalation.enable', 'Enable')}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default EscalationPolicyForm;
